'use client';

import { Fragment, useEffect, useMemo, useRef, type MouseEvent } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { getImageUrlForVariant, localizeUrl, stripHtml } from '@/lib/homepage-mappers';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import CmsImage from '@/components/ui/CmsImage';
import { scrollToHashTarget } from '@/components/util/GsapSmoothScroller';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import type { ImageGridComponent } from '@/types/product';

interface ProductsIntroStatsSectionProps {
  description?: string;
  imageGridComponent?: ImageGridComponent;
  locale?: string;
}

type BentoItem = {
  title: string;
  imageSrc: string;
};

const DEFAULT_PRODUCT_RANGE_HASH = '#product-range';

/** Resolve CMS buttonUrl into a hash for in-page scroll, or a localized page URL. */
function resolveCtaHref(buttonUrl: string | null | undefined, locale: string): string {
  const raw = buttonUrl?.trim();
  if (!raw) return DEFAULT_PRODUCT_RANGE_HASH;
  if (raw.startsWith('#')) return raw;
  if (/^(https?:|mailto:|tel:)/i.test(raw)) return raw;
  // Bare section id from CMS (e.g. "product-range") → in-page hash
  if (!raw.includes('/')) return `#${raw.replace(/^#/, '')}`;
  return localizeUrl(raw, locale) || DEFAULT_PRODUCT_RANGE_HASH;
}

function getBentoItems(imageGridComponent?: ImageGridComponent): BentoItem[] {
  const gridItems = imageGridComponent?.mediaGrid?.filter((item) => item.media?.url) ?? [];
  return gridItems
    .map((item, index) => {
      const imageSrc =
        getImageUrlForVariant(item.media as never, 'large') ||
        getImageUrlForVariant(item.media as never, 'medium') ||
        '';

      return {
        title: item.heading?.trim() || `grid${index + 1}`,
        imageSrc,
      };
    })
    .filter((item) => item.imageSrc);
}

export default function ProductsIntroStatsSection({
  description,
  imageGridComponent,
  locale = 'en',
}: ProductsIntroStatsSectionProps) {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const textRef = useRef<HTMLDivElement | null>(null);

  const introText = useMemo(() => {
    const fromGrid = [
      imageGridComponent?.heading?.trim(),
      stripHtml(imageGridComponent?.subHeading),
    ]
      .filter(Boolean)
      .join(' ');
    const fromApi = stripHtml(description);
    return fromGrid || fromApi || '';
  }, [description, imageGridComponent]);

  const ctaButton = imageGridComponent?.button?.[0];
  const ctaText = ctaButton?.buttonText?.trim();
  const ctaHref = resolveCtaHref(ctaButton?.buttonUrl, locale);
  const ctaHash = ctaHref.includes('#') ? `#${ctaHref.split('#').pop()}` : null;
  const isInPageHash = Boolean(ctaHash && ctaHash.length > 1);
  const ctaLinkProps = getCmsButtonLinkProps(ctaButton);
  const shouldSmoothScroll = isInPageHash && ctaLinkProps.target !== '_blank';
  const ctaArrowDirection = isInPageHash ? ('down' as const) : undefined;

  const handleCtaClick = (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (!shouldSmoothScroll || !ctaHash) return;
    event.preventDefault();
    scrollToHashTarget(ctaHash, true, true);
  };

  const introWords = useMemo(() => introText.split(/\s+/).filter(Boolean), [introText]);
  const leadWords = useMemo(() => {
    const [firstSentence] = introText.match(/.*?[.!?](\s|$)/) || [];
    return (firstSentence?.trim() || introText).split(/\s+/).filter(Boolean);
  }, [introText]);
  const bodyWords = useMemo(() => {
    const leadText = leadWords.join(' ');
    return introText.slice(leadText.length).trim().split(/\s+/).filter(Boolean);
  }, [leadWords, introText]);
  const bentoItems = useMemo(() => getBentoItems(imageGridComponent), [imageGridComponent]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!sectionRef.current || !textRef.current) return;

    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      const words = textRef.current?.querySelectorAll('[data-word]');
      const tiles = gsap.utils.toArray<HTMLElement>('[data-product-bento-tile]');

      if (words?.length) {
        gsap.set(words, { color: 'rgba(255, 255, 255, 0.28)' });
        gsap.to(words, {
          color: '#ffffff',
          ease: 'none',
          stagger: 0.035,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            end: 'top 38%',
            scrub: 1,
          },
        });
      }

      if (tiles.length) {
        gsap.set(tiles, { autoAlpha: 0, y: 34, scale: 0.97 });
        gsap.to(tiles, {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          ease: 'power3.out',
          stagger: 0.09,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 62%',
            once: true,
          },
        });
      }
    }, sectionRef);

    return () => {
      ctx.revert();
    };
  }, [bentoItems.length, introWords.length]);

  if (!introText && !bentoItems.length) return null;

  return (
    <section className="relative z-10 overflow-hidden bg-black">
      <div
        ref={sectionRef}
        className="container relative mx-auto grid gap-8 px-4 sm:px-6 lg:grid-cols-[minmax(0,0.92fr)_minmax(440px,1.08fr)] lg:items-center lg:gap-12 lg:px-0"
      >
        <div className="max-w-[660px]">
          <span className="block h-[3px] w-12 bg-[#ed1c24]" aria-hidden />
          <div ref={textRef} className="mt-8">
            <p className="text-[1rem] font-bold leading-[1.28] text-white sm:text-[2rem] md:text-[1.8rem] lg:text-[1.9rem] xl:text-[2rem]">
              {leadWords.map((word, index) => (
                <Fragment key={index}>
                  <span data-word>{word}</span>
                  {index < leadWords.length - 1 ? ' ' : ''}
                </Fragment>
              ))}
            </p>
            {bodyWords.length ? (
              <p className="mt-6 max-w-[620px] text-base font-medium leading-[1.75] text-white sm:text-lg lg:text-[1rem] xl:text-[1rem]">
                {bodyWords.map((word, index) => (
                  <Fragment key={index}>
                    <span data-word>{word}</span>
                    {index < bodyWords.length - 1 ? ' ' : ''}
                  </Fragment>
                ))}
              </p>
            ) : null}
          </div>
          <div className="mt-8">
            {(() => {
              if (!ctaText) return null;
              if (shouldSmoothScroll) {
                return (
                  <SwapTextButton
                    text={ctaText}
                    arrowDirection={ctaArrowDirection}
                    onClick={handleCtaClick}
                    aria-label={ctaText}
                    className="rounded-md px-5 py-3 shadow-none"
                  />
                );
              }
              return (
                <SwapTextButton
                  text={ctaText}
                  href={ctaHref}
                  {...ctaLinkProps}
                  arrowDirection={ctaArrowDirection}
                  className="rounded-md px-5 py-3 shadow-none"
                />
              );
            })()}
          </div>
        </div>

        <div className="relative">
          <div className="grid min-h-[430px] grid-cols-2 grid-rows-[1.05fr_0.9fr_1fr] gap-3 sm:min-h-[500px] lg:min-h-[460px] xl:min-h-[500px]">
            {bentoItems.map((item, index) => (
              <div
                key={item.title}
                data-product-bento-tile
                className={[
                  'group relative overflow-hidden rounded-lg border border-white/10 bg-[#101010]',
                  index === 0 ? 'row-span-2' : '',
                  index === 1 ? 'row-span-1' : '',
                  index === 2 ? 'row-span-2' : '',
                  index === 3 ? 'row-span-1' : '',
                ].join(' ')}
              >
                <CmsImage
                  src={item.imageSrc}
                  alt={item.title}
                  fill
                  className="object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                  sizes="(min-width: 1024px) 280px, 50vw"
                  fallbackSrc={MEDIA_PLACEHOLDER}
                />
                <div
                  className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,transparent_35%,rgba(0,0,0,0.72)_100%)]"
                  aria-hidden
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
